import IconButton from '@/components/ui/custom/buttons/IconButton';
import MainButton from '@/components/ui/custom/buttons/MainButton';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import Text from '@/components/ui/custom/dataDisplay/Text';
import { LoadingIcon } from '@/components/icons';
import { REQUEST_STALE_TIME_1_DAY_IN_MS } from '@/constants';
import { projectsMetadaraQuerykeys } from '@/data/services/querykeys';
import { Project } from '@/models/Project';
import { ArrowsPointingOutIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'motion/react';
import { debounce } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { twJoin } from 'tailwind-merge';
import ProjectEmbedPreview from './ProjectEmbedPreview';

type ProjectRuntimeProps = Pick<Project, 'title' | 'projectUrl' | 'tool'> & {
  fullScreen?: boolean;
};

const ProjectRuntime = ({ title, projectUrl, tool }: ProjectRuntimeProps) => {
  const [fullScreen, setFullscren] = useState(false);
  const { t } = useTranslation('translation', {
    keyPrefix: 'klassView.fullScreen',
  });

  const {
    isInitialLoading: isLoading,
    data: projectMetadata,
    error,
  } = useQuery({
    ...projectsMetadaraQuerykeys.get({
      url: projectUrl || '',
      tool: tool.name,
    }),
    staleTime: REQUEST_STALE_TIME_1_DAY_IN_MS,
    retry: false,
    enabled: !!projectUrl,
  });

  const container = useRef<HTMLDivElement>(null);

  const onFullcreenOpen = () => {
    type HTMLDivElementMultplatform = HTMLDivElement & {
      requestFullscreen?: () => Promise<void>;
      webkitRequestFullscreen?: () => Promise<void>;
      mozRequestFullScreen?: () => Promise<void>;
      msRequestFullscreen?: () => Promise<void>;
    };

    const element: HTMLDivElementMultplatform | null = container.current;

    const requestFullscreen =
      element?.requestFullscreen ||
      element?.webkitRequestFullscreen ||
      element?.mozRequestFullScreen ||
      element?.msRequestFullscreen;
    requestFullscreen?.call(element);
  };

  const onFullcreenExit = () => {
    if (document.fullscreenElement) document.exitFullscreen();
  };

  useEffect(() => {
    const handleFullscreen = debounce(() => {
      setFullscren(!!document.fullscreenElement);
    }, 200);
    document.addEventListener('fullscreenchange', handleFullscreen);
  }, []);

  useEffect(() => {
    document.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key === 'F11') {
        e.preventDefault();
        onFullcreenOpen();
      }
    });
  }, []);

  return (
    <div className="flex flex-col gap-2.5" ref={container}>
      <motion.div
        className={twJoin(
          'relative flex items-center justify-between',
          fullScreen && 'py-3.5 px-5 absolute inset-0 z-20 bg-base-100',
        )}
        layout
      >
        <Text text={title} format="rubik-500" className="!text-primary" />
        <ConditionalRenderer condition={!fullScreen}>
          <IconButton
            icon={<ArrowsPointingOutIcon className="w-5 h-5 text-primary" />}
            tooltip="Tela cheia (F11)"
            onClick={onFullcreenOpen}
          />
        </ConditionalRenderer>

        <ConditionalRenderer condition={fullScreen}>
          <MainButton
            className="text-error gap-1"
            icon={<XMarkIcon />}
            color="custom"
            text={t('exit')}
            onClick={onFullcreenExit}
          />
        </ConditionalRenderer>
      </motion.div>
      <ConditionalRenderer
        condition={!isLoading}
        fallback={
          <div className="h-60 flex items-center justify-center">
            <LoadingIcon className="w-10 md:w-20 mx-auto text-primary" />
          </div>
        }
      >
        <ConditionalRenderer condition={!fullScreen && projectUrl}>
          <ProjectEmbedPreview
            className={{ iframe: 'w-full aspect-video' }}
            src={projectUrl || ''}
            projectMetadata={{ value: projectMetadata, error }}
          />
        </ConditionalRenderer>
        <ConditionalRenderer condition={fullScreen && projectUrl}>
          <ProjectEmbedPreview
            className={{
              iframe: 'h-svh aspect-video',
              base: 'w-svw h-svh absolute',
            }}
            src={projectUrl || ''}
            projectMetadata={{ value: projectMetadata, error }}
          />
        </ConditionalRenderer>
      </ConditionalRenderer>
    </div>
  );
};

export default ProjectRuntime;
